import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import QRCode from 'react-qr-code';
import WalletLedgerDialog from '../components/WalletLedgerDialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { UserAvatar } from '../components/UserAvatar';
import {
  LayoutDashboard, Users, Wallet, KeyRound, Gamepad2, Bell, Palette,
  Gift, UserCog, Check, X, Link2, Copy, ExternalLink, Building2,
  BarChart3, Trophy, Plus, Trash2, Timer, TrendingUp, Calendar, ShieldAlert, AlertOctagon, AlertTriangle,
  UserPlus, Power, Eye, Coins, FileBarChart
} from 'lucide-react';

const THEMES = { 1: "Neon Cyber", 2: "Royal Gold", 3: "Ocean Deep", 4: "Sunset Blaze", 5: "Forest", 6: "Cherry", 7: "Arctic", 8: "Midnight", 9: "Ember", 10: "Violet" };
const FONTS = { 1: "Unbounded + Outfit", 2: "Rajdhani + Exo 2", 3: "Orbitron + Roboto", 4: "Chakra Petch + Nunito", 5: "Teko + Poppins" };

function StatusBadge({ status }) {
  const cls = status === 'pending' ? 'badge-pending' : status === 'approved' || status === 'link_generated' ? 'badge-approved' : 'badge-rejected';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls}`}>{status}</span>;
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl p-5 mb-4" style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.05)' }}>
      {title && (
        <h3 className="flex items-center gap-2 text-base font-bold mb-4" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-text)' }}>
          {Icon && <Icon size={16} style={{ color: 'var(--neon-primary)' }} />} {title}
        </h3>
      )}
      {children}
    </div>
  );
}

function ApproveRejectDialog({ open, onClose, onSubmit, title }) {
  const [msg, setMsg] = useState('');
  const [processing, setProcessing] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <DialogHeader><DialogTitle style={{ color: 'var(--neon-primary)', fontFamily: 'var(--font-heading)' }}>{title}</DialogTitle></DialogHeader>
        <textarea data-testid="admin-message-input" className="neon-input w-full h-20 p-3 rounded-lg resize-none text-sm" placeholder="Message to user (required for reject)"
          value={msg} onChange={(e) => setMsg(e.target.value)}
          style={{ background: 'var(--neon-elevated)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }} />
        <div className="flex gap-3">
          <button data-testid="dialog-approve-btn" disabled={processing} onClick={async () => {
            setProcessing(true); await onSubmit('approve', msg); onClose(); setMsg(''); setProcessing(false);
          }} className="flex-1 py-2 rounded-lg text-sm font-bold disabled:opacity-50" style={{ background: 'rgba(1,255,195,0.15)', color: '#01FFC3' }}>
            {processing ? 'Processing...' : 'Approve'}
          </button>
          <button data-testid="dialog-reject-btn" disabled={processing} onClick={async () => {
            if (!msg) { toast.error('Message required for reject'); return; }
            setProcessing(true); await onSubmit('reject', msg); onClose(); setMsg(''); setProcessing(false);
          }} className="flex-1 py-2 rounded-lg text-sm font-bold disabled:opacity-50" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>
            {processing ? 'Processing...' : 'Reject'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Dashboard =====
function AdminDashboard({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [pendingResults, setPendingResults] = useState([]);
  useEffect(() => {
    api.get('/admin/dashboard').then(r => setStats(r.data)).catch(() => {});
    api.get('/admin/pending-results').then(r => setPendingResults(r.data.pending_results || [])).catch(() => {});
    const interval = setInterval(() => {
      api.get('/admin/dashboard').then(r => setStats(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  if (!stats) return <p style={{ color: 'var(--neon-text-muted)' }}>Loading...</p>;
  const items = [
    { label: 'Users', value: stats.total_users, color: 'var(--neon-primary)', tab: 'users' },
    { label: 'Sub-Admins', value: stats.total_sub_admins || 0, color: '#AB47BC', tab: 'sub-admins' },
    { label: 'Pending Wallet', value: stats.pending_wallet, color: 'var(--neon-accent)', tab: 'wallet' },
    { label: 'SA Wallet', value: stats.pending_sub_admin_wallet || 0, color: '#FF8F00', tab: 'sa-wallet' },
    { label: 'Pending Reset', value: stats.pending_reset, color: 'var(--neon-secondary)', tab: 'reset' },
    { label: 'Profile Changes', value: stats.pending_profile, color: '#AB47BC', tab: 'profile' },
    { label: 'Fraud Alerts', value: stats.fraud_attempts, color: '#FF2A6D', tab: 'fraud' },
    { label: "Today's Profit", value: stats.today_profit, color: stats.today_profit >= 0 ? '#01FFC3' : '#FF2A6D', tab: 'analytics' },
  ];
  return (
    <div className="space-y-4">
      {/* Pending Results Alert */}
      {pendingResults.length > 0 && (
        <div data-testid="pending-results-alert" className="p-4 rounded-xl" style={{ background: 'rgba(255,42,109,0.08)', border: '1px solid rgba(255,42,109,0.25)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2" style={{ color: '#FF2A6D' }}>
              <AlertTriangle size={16} /> Pending Results ({pendingResults.length})
            </h3>
            <button onClick={() => onNavigate('results')} className="text-xs px-3 py-1 rounded-lg font-bold"
              style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>
              View All
            </button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {pendingResults.slice(0, 5).map((p, idx) => (
              <div key={`${p.company_id}-${p.date}`} className="flex items-center justify-between p-2 rounded-lg"
                style={{ background: 'var(--neon-surface)' }}>
                <div>
                  <span className="text-sm font-bold" style={{ color: 'var(--neon-text)' }}>{p.company_name}</span>
                  <span className="text-xs ml-2" style={{ color: 'var(--neon-text-muted)' }}>{p.date}</span>
                  {p.has_bets && <span className="text-[10px] ml-2 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700' }}>{p.bet_count} bets</span>}
                </div>
                <span className="text-[10px] font-bold px-2 py-1 rounded"
                  style={{ background: p.days_pending >= 3 ? 'rgba(255,42,109,0.2)' : 'rgba(255,215,0,0.15)', color: p.days_pending >= 3 ? '#FF2A6D' : '#FFD700' }}>
                  {p.days_pending === 0 ? 'Today' : `${p.days_pending}d ago`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it, i) => (
          <div key={it.label} data-testid={`stat-${i}`}
            onClick={() => onNavigate(it.tab)}
            className="p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.03] active:scale-95"
            style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.05)' }}>
            <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>{it.label}</p>
            <p className="text-2xl font-black mt-1" style={{ fontFamily: 'var(--font-heading)', color: it.color }}>{it.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== Companies =====
function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [form, setForm] = useState({ name: '', short_name: '', timer_minutes: 60, result_time: '', bet_open_time: '', valid_until: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fetch = useCallback(() => {
    api.get('/admin/companies').then(r => setCompanies(r.data.companies)).catch(() => {});
    api.get('/admin/pending-companies').then(r => setPendingCompanies(r.data.companies || [])).catch(() => {});
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const add = async () => {
    if (!form.name) return;
    try { await api.post('/admin/companies', form); toast.success('Company added'); setForm({ name: '', short_name: '', timer_minutes: 60, result_time: '', bet_open_time: '', valid_until: '' }); fetch(); }
    catch (e) { toast.error('Failed'); }
  };
  const toggleTimer = async (c) => {
    try { await api.put(`/admin/companies/${c.id}`, { timer_active: !c.timer_active, timer_minutes: c.timer_minutes }); toast.success('Timer updated'); fetch(); }
    catch { toast.error('Failed'); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/companies/${id}`); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed'); }
  };
  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ result_time: c.result_time || '', timer_minutes: c.timer_minutes || 60, bet_open_time: c.bet_open_time || '', valid_until: c.valid_until || '' });
  };
  const saveEdit = async (id) => {
    try { await api.put(`/admin/companies/${id}`, editForm); toast.success('Updated'); setEditingId(null); fetch(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Pending Company Approvals */}
      {pendingCompanies.length > 0 && (
        <SectionCard title={`Pending Approvals (${pendingCompanies.length})`} icon={AlertTriangle}>
          <div className="space-y-2">
            {pendingCompanies.map(c => (
              <div key={c.id} className="p-3 rounded-xl flex items-center justify-between"
                style={{ background: 'rgba(255,42,109,0.05)', border: '1px solid rgba(255,42,109,0.2)' }}>
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{c.name}</p>
                  <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>
                    By: {c.created_by_name || 'Sub-Admin'} | Result: {c.result_time || '—'} | Close: {c.closing_time || '—'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`approve-co-${c.id}`} onClick={async () => {
                    try { await api.put(`/admin/companies/${c.id}/approve`); toast.success('Approved!'); fetch(); } catch { toast.error('Failed'); }
                  }} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(1,255,195,0.15)', color: '#01FFC3' }}>
                    Approve
                  </button>
                  <button data-testid={`reject-co-${c.id}`} onClick={async () => {
                    try { await api.put(`/admin/companies/${c.id}/reject`); toast.success('Rejected'); fetch(); } catch { toast.error('Failed'); }
                  }} className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Add Company" icon={Plus}>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="company-name-input" className="neon-input" placeholder="Company name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input data-testid="company-short-input" className="neon-input" placeholder="Short name" value={form.short_name} onChange={(e) => setForm(f => ({ ...f, short_name: e.target.value }))} />
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Result Time</Label>
            <input data-testid="company-result-time-input" type="time" className="w-full p-2 rounded-lg text-sm"
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
              value={form.result_time} onChange={(e) => setForm(f => ({ ...f, result_time: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Bet Open Time</Label>
            <input data-testid="company-bet-open-time-input" type="time" className="w-full p-2 rounded-lg text-sm"
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
              value={form.bet_open_time} onChange={(e) => setForm(f => ({ ...f, bet_open_time: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Valid Until (Date)</Label>
            <input data-testid="company-valid-until-input" type="date" className="w-full p-2 rounded-lg text-sm"
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
              value={form.valid_until} onChange={(e) => setForm(f => ({ ...f, valid_until: e.target.value }))}
              min={new Date().toISOString().split('T')[0]} />
          </div>
        </div>
        {form.result_time && /^\d{2}:\d{2}$/.test(form.result_time) && (
          <p className="text-xs mt-1" style={{ color: 'var(--neon-accent)' }}>
            Closing Time (auto): {(() => { const [h, m] = form.result_time.split(':').map(Number); let t = h * 60 + m - 10; if (t < 0) t += 1440; return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`; })()}
            {form.bet_open_time && <span> | Opens: {form.bet_open_time}</span>}
            {form.valid_until && <span> | Valid until: {form.valid_until}</span>}
          </p>
        )}
        <button data-testid="add-company-btn" onClick={add} className="btn-neon-primary mt-3 text-sm">Add Company</button>
      </SectionCard>
      <SectionCard title="Companies" icon={Building2}>
        {companies.map(c => (
          <div key={c.id} data-testid={`company-row-${c.id}`} className="p-3 rounded-lg mb-2" style={{ background: 'var(--neon-surface)' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{c.name} <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>({c.short_name})</span></p>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>
                  Timer: {c.timer_minutes}min | Close: {c.closing_time || 'Not set'} | Result: {c.result_time || 'Not set'}{c.bet_open_time ? ` | Opens: ${c.bet_open_time}` : ''}{c.valid_until ? ` | Until: ${c.valid_until}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`edit-company-${c.id}`} onClick={() => editingId === c.id ? setEditingId(null) : startEdit(c)}
                  className="px-2 py-1 rounded text-xs font-bold"
                  style={{ background: 'rgba(5,217,232,0.1)', color: 'var(--neon-primary)' }}>
                  {editingId === c.id ? 'Cancel' : 'Edit'}
                </button>
                <button data-testid={`toggle-timer-${c.id}`} onClick={() => toggleTimer(c)}
                  className="px-3 py-1 rounded text-xs font-bold"
                  style={{ background: c.timer_active ? 'rgba(1,255,195,0.15)' : 'rgba(255,255,255,0.05)', color: c.timer_active ? '#01FFC3' : 'var(--neon-text-muted)' }}>
                  <Timer size={10} className="inline mr-1" />{c.timer_active ? 'ON' : 'OFF'}
                </button>
                <button data-testid={`delete-company-${c.id}`} onClick={() => remove(c.id)} className="p-1 rounded" style={{ color: 'var(--neon-secondary)' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {editingId === c.id && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Result Time</Label>
                  <input data-testid={`edit-result-${c.id}`} type="time" className="w-full p-2 rounded-lg text-sm"
                    style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
                    value={editForm.result_time} onChange={(e) => setEditForm(f => ({ ...f, result_time: e.target.value }))} />
                  {editForm.result_time && /^\d{2}:\d{2}$/.test(editForm.result_time) && (
                    <p className="text-[10px] mt-1" style={{ color: 'var(--neon-accent)' }}>
                      Closing: {(() => { const [h, m] = editForm.result_time.split(':').map(Number); let t = h * 60 + m - 10; if (t < 0) t += 1440; return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`; })()}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Bet Open Time</Label>
                  <input data-testid={`edit-bet-open-${c.id}`} type="time" className="w-full p-2 rounded-lg text-sm"
                    style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
                    value={editForm.bet_open_time || ''} onChange={(e) => setEditForm(f => ({ ...f, bet_open_time: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Valid Until</Label>
                  <input data-testid={`edit-valid-${c.id}`} type="date" className="w-full p-2 rounded-lg text-sm"
                    style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
                    value={editForm.valid_until || ''} onChange={(e) => setEditForm(f => ({ ...f, valid_until: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]} />
                </div>
                <button data-testid={`save-edit-${c.id}`} onClick={() => saveEdit(c.id)} className="btn-neon-primary text-xs col-span-2">
                  Save Changes
                </button>
              </div>
            )}
          </div>
        ))}
      </SectionCard>
    </div>
  );
}

// ===== Wallet Requests =====
function AdminWallet() {
  const [reqs, setReqs] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });
  const fetch = useCallback(() => { api.get('/admin/wallet-requests').then(r => setReqs(r.data.requests)).catch(() => {}); }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handle = async (action, msg) => {
    try { await api.put(`/admin/wallet-requests/${dialog.id}`, { action, admin_message: msg }); toast.success(`${action}d`); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const getScreenshotUrl = (path) => {
    if (!path) return null;
    const token = localStorage.getItem('neonplay_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  return (
    <div className="space-y-3">
      <ApproveRejectDialog open={dialog.open} onClose={() => setDialog({ open: false, id: null })} onSubmit={handle} title="Wallet Request Action" />
      {reqs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No requests</p>}
      {reqs.map(r => (
        <SectionCard key={r.id}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{r.user_name}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{r.user_email}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg" style={{ color: r.type === 'add' ? 'var(--neon-primary)' : 'var(--neon-secondary)' }}>
                {r.type === 'add' ? '+' : '-'}{r.amount}
              </p>
              <p className="text-xs uppercase font-bold" style={{ color: 'var(--neon-text-muted)' }}>{r.type}</p>
            </div>
          </div>
          {r.utr_number && <p className="text-xs mb-1" style={{ color: 'var(--neon-text-secondary)' }}>UTR: <strong>{r.utr_number}</strong></p>}
          {r.screenshot_path && (
            <div className="mb-2">
              <img src={getScreenshotUrl(r.screenshot_path)} alt="Screenshot" className="max-h-32 rounded-lg border" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            </div>
          )}
          {r.type === 'withdraw' && (r.user_upi || r.user_account) && (
            <div className="p-3 rounded-lg mb-2 text-xs" style={{ background: 'var(--neon-bg)' }}>
              {r.user_upi && (
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="mb-1" style={{ color: 'var(--neon-text-secondary)' }}>UPI ID:</p>
                    <p className="font-bold break-all mb-2" style={{ color: 'var(--neon-accent)' }}>{r.user_upi}</p>
                    <button
                      data-testid={`copy-upi-${r.id}`}
                      onClick={() => { navigator.clipboard.writeText(r.user_upi); toast.success('UPI ID copied'); }}
                      className="text-[10px] px-2 py-1 rounded font-bold"
                      style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}
                    >
                      <Copy size={10} className="inline mr-1" />Copy UPI
                    </button>
                  </div>
                  <div className="shrink-0 p-2 rounded-lg bg-white" data-testid={`qr-upi-${r.id}`}>
                    <QRCode
                      value={`upi://pay?pa=${encodeURIComponent(r.user_upi)}&pn=${encodeURIComponent(r.user_name || 'User')}&am=${r.amount}&cu=INR&tn=Mukamari%20Withdrawal`}
                      size={96}
                      level="M"
                    />
                    <p className="text-[9px] text-center mt-1 font-bold" style={{ color: '#000' }}>Scan & Pay ₹{r.amount}</p>
                  </div>
                </div>
              )}
              {r.user_account && <p style={{ color: 'var(--neon-text-secondary)' }}>Acc: <strong>{r.user_account}</strong> | IFSC: <strong>{r.user_ifsc}</strong></p>}
              {r.user_account_holder && <p style={{ color: 'var(--neon-text-secondary)' }}>Holder: <strong>{r.user_account_holder}</strong></p>}
            </div>
          )}
          {r.note && <p className="text-xs mb-2" style={{ color: 'var(--neon-text-muted)' }}>Note: {r.note}</p>}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{new Date(r.created_at).toLocaleString()}</span>
            </div>
            {r.status === 'pending' && (
              <button data-testid={`action-wallet-${r.id}`} onClick={() => setDialog({ open: true, id: r.id })}
                className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>
                Action
              </button>
            )}
          </div>
          {r.admin_message && <p className="mt-2 text-xs p-2 rounded" style={{ background: 'var(--neon-bg)', color: 'var(--neon-text-secondary)' }}>Admin msg: {r.admin_message}</p>}
        </SectionCard>
      ))}
    </div>
  );
}

// ===== Password Reset =====
function AdminPasswordReset() {
  const [reqs, setReqs] = useState([]);
  const [links, setLinks] = useState({});
  const fetch = useCallback(() => { api.get('/admin/password-reset-requests').then(r => setReqs(r.data.requests)).catch(() => {}); }, []);
  useEffect(() => { fetch(); }, [fetch]);
  const gen = async (id) => {
    try { 
      const res = await api.post(`/admin/password-reset/${id}/generate-link`); 
      setLinks(l => ({ ...l, [id]: res.data })); 
      // Update local status without re-fetching (request stays visible with link)
      setReqs(prev => prev.map(r => r.id === id ? { ...r, status: 'link_generated', reset_link: res.data.reset_link } : r));
      toast.success('Link generated! Copy or share via WhatsApp'); 
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3">
      {reqs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No requests</p>}
      {reqs.map(r => (
        <SectionCard key={r.id}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{r.user_name}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-secondary)' }}>{r.contact_type}: {r.contact_info}</p>
            </div>
            <StatusBadge status={r.status} />
          </div>
          {r.status === 'pending' && <button data-testid={`gen-link-${r.id}`} onClick={() => gen(r.id)} className="btn-neon-primary text-xs px-4 py-2"><Link2 size={12} className="inline mr-1" /> Generate Link</button>}
          {(links[r.id] || r.reset_link) && (
            <div className="mt-2 p-3 rounded-lg" style={{ background: 'var(--neon-bg)' }}>
              <p className="text-xs break-all mb-2" style={{ color: 'var(--neon-primary)' }}>{links[r.id]?.reset_link || r.reset_link}</p>
              <div className="flex gap-2">
                <button data-testid={`copy-link-${r.id}`} onClick={() => { navigator.clipboard.writeText(links[r.id]?.reset_link || r.reset_link); toast.success('Copied!'); }}
                  className="flex items-center gap-1 px-3 py-1 rounded text-xs font-bold" style={{ background: 'var(--neon-surface)', color: 'var(--neon-primary)' }}>
                  <Copy size={10} /> Copy
                </button>
                {(links[r.id]?.whatsapp_link || r.contact_type === 'whatsapp') && (
                  <a data-testid={`wa-link-${r.id}`} href={links[r.id]?.whatsapp_link || `https://wa.me/91${r.contact_info.replace(/[+ \-]/g, '').replace(/^91/, '')}?text=${encodeURIComponent('Your reset link: ' + r.reset_link)}`}
                    target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(37,211,102,0.15)', color: '#25D366' }}>
                    <ExternalLink size={10} /> WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}
        </SectionCard>
      ))}
    </div>
  );
}

// ===== Game Settings =====
function AdminGameSettings() {
  const [settings, setSettings] = useState({ options: [], bet_amounts: [], multipliers: {} });
  const [amtText, setAmtText] = useState('');
  const [newOpt, setNewOpt] = useState({ name: '', input_type: 'single', multiplier: 10 });
  const INPUT_TYPES = [
    { value: 'single', label: 'Single (0-9)' },
    { value: 'jodi', label: 'Jodi (00-99)' },
    { value: 'cross', label: 'Cross (2 digits)' },
    { value: 'single_patti', label: 'Single Patti (3 diff digits)' },
    { value: 'double_patti', label: 'Double Patti (2 same digits)' },
    { value: 'triple_patti', label: 'Triple Patti (3 same digits)' },
  ];
  const fetchSettings = useCallback(() => {
    api.get('/admin/game-settings').then(r => {
      setSettings(r.data);
      setAmtText(r.data.bet_amounts?.join(', ') || '');
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggleOption = async (idx) => {
    try { await api.put(`/admin/game-options/${idx}/toggle`); toast.success('Toggled'); fetchSettings(); }
    catch { toast.error('Failed'); }
  };
  const deleteOption = async (idx) => {
    try { await api.delete(`/admin/game-options/${idx}`); toast.success('Deleted'); fetchSettings(); }
    catch { toast.error('Failed'); }
  };
  const addOption = async () => {
    if (!newOpt.name.trim()) { toast.error('Name required'); return; }
    try {
      await api.post('/admin/game-options', newOpt);
      toast.success('Option added');
      setNewOpt({ name: '', input_type: 'single', multiplier: 10 });
      fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const saveAmounts = async () => {
    try {
      await api.put('/admin/game-settings', { bet_amounts: amtText.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n)) });
      toast.success('Bet amounts saved'); fetchSettings();
    } catch { toast.error('Failed'); }
  };
  const saveMultiplier = async (opt) => {
    const name = opt.name;
    const mult = opt.multiplier;
    const updatedMultipliers = { ...settings.multipliers, [name]: mult };
    const updatedOptions = settings.options.map(o => o.name === name ? { ...o, multiplier: mult } : o);
    try {
      await api.put('/admin/game-settings', { multipliers: updatedMultipliers, options: updatedOptions });
      toast.success(`${name} multiplier saved`); fetchSettings();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Current Options */}
      <SectionCard title="Game Options (Enable/Disable/Delete)" icon={Gamepad2}>
        <div className="space-y-2">
          {settings.options?.map((opt, idx) => {
            const o = typeof opt === 'object' ? opt : { name: opt, input_type: 'single', multiplier: 10, enabled: true };
            return (
              <div key={idx} data-testid={`game-opt-${idx}`} className="p-3 rounded-xl flex items-center justify-between"
                style={{ background: o.enabled !== false ? 'var(--neon-surface)' : 'rgba(255,255,255,0.02)', border: o.enabled !== false ? '1px solid rgba(1,255,195,0.1)' : '1px solid rgba(255,42,109,0.1)', opacity: o.enabled !== false ? 1 : 0.5 }}>
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{o.name}</p>
                  <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>{o.input_type} | {o.multiplier}x</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input className="neon-input w-16 text-xs text-center" type="number" value={o.multiplier}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setSettings(s => ({ ...s, options: s.options.map((op, i) => i === idx ? { ...op, multiplier: val } : op) }));
                    }} />
                  <button className="text-xs px-1" style={{ color: 'var(--neon-accent)' }} onClick={() => saveMultiplier(settings.options[idx])}>Save</button>
                  <button data-testid={`toggle-opt-${idx}`} onClick={() => toggleOption(idx)}
                    className="px-2 py-1 rounded text-xs font-bold"
                    style={{ background: o.enabled !== false ? 'rgba(255,42,109,0.1)' : 'rgba(1,255,195,0.1)', color: o.enabled !== false ? '#FF2A6D' : '#01FFC3' }}>
                    {o.enabled !== false ? 'Disable' : 'Enable'}
                  </button>
                  <button data-testid={`delete-opt-${idx}`} onClick={() => deleteOption(idx)}>
                    <Trash2 size={14} style={{ color: 'var(--neon-secondary)' }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Add New Option */}
      <SectionCard title="Add New Option" icon={Plus}>
        <div className="grid grid-cols-3 gap-3">
          <Input data-testid="new-opt-name" className="neon-input" placeholder="Name" value={newOpt.name}
            onChange={(e) => setNewOpt(n => ({ ...n, name: e.target.value }))} />
          <select data-testid="new-opt-type" className="neon-input rounded-lg px-3 text-sm"
            style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }}
            value={newOpt.input_type} onChange={(e) => setNewOpt(n => ({ ...n, input_type: e.target.value }))}>
            {INPUT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Input data-testid="new-opt-mult" className="neon-input" type="number" placeholder="Multiplier"
            value={newOpt.multiplier} onChange={(e) => setNewOpt(n => ({ ...n, multiplier: parseInt(e.target.value) || 0 }))} />
        </div>
        <button data-testid="add-opt-btn" onClick={addOption} className="btn-neon-primary text-sm mt-3">Add Option</button>
      </SectionCard>

      {/* Bet Amounts */}
      <SectionCard title="Bet Amounts" icon={Coins}>
        <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Comma separated</Label>
        <Input data-testid="bet-amounts-input" className="neon-input" value={amtText} onChange={(e) => setAmtText(e.target.value)} />
        <button data-testid="save-amounts-btn" onClick={saveAmounts} className="btn-neon-primary text-sm mt-3">Save Amounts</button>
      </SectionCard>
    </div>
  );
}

// ===== Results =====
function AdminResults() {
  const [results, setResults] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ company_id: '', company_name: '', winning_number: '', date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })() });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null, action: '' });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchData = useCallback(() => {
    Promise.all([api.get('/admin/results'), api.get('/admin/companies')]).then(([rr, cr]) => {
      setResults(rr.data.results);
      setCompanies(cr.data.companies);
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const declare = async () => {
    if (!form.company_id || !form.winning_number) { toast.error('Company aur Winning Number dono zaruri hai!'); return; }
    const num = parseInt(form.winning_number);
    if (isNaN(num) || num < 0 || num > 99) { toast.error('Winning Number 00-99 ke beech hona chahiye'); return; }
    try {
      await api.post('/admin/results', { ...form, winning_number: num, winning_option: 'auto' });
      toast.success('Result created! Preview dekho aur Confirm karo.');
      setForm(f => ({ ...f, winning_number: '' }));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const loadPreview = async (id) => {
    setPreviewLoading(true);
    try {
      const res = await api.get(`/admin/results/${id}/preview`);
      setPreview({ id, ...res.data });
    } catch { toast.error('Preview load failed'); }
    finally { setPreviewLoading(false); }
  };

  const confirmResult = async (id) => {
    try {
      const res = await api.put(`/admin/results/${id}/confirm`);
      toast.success(res.data.message);
      setPreview(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const revert = async (id) => {
    try { await api.put(`/admin/results/${id}/revert`); toast.success('Result reverted'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Simple Declare Form */}
      <SectionCard title="Declare Result" icon={Trophy}>
        <p className="text-xs mb-3 p-2 rounded-lg" style={{ background: 'rgba(1,255,195,0.05)', color: 'var(--neon-text-secondary)' }}>
          Sirf Company aur Winning Number dalo. Jodi, Single, Hurf, Cross — sab automatically match hoga!
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select data-testid="result-company-select" value={form.company_id}
            onChange={(e) => { const c = companies.find(x => x.id === e.target.value); setForm(f => ({ ...f, company_id: e.target.value, company_name: c?.name || '' })); }}
            className="neon-input p-3 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <option value="">Select Company</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <Input data-testid="result-number-input" className="neon-input text-center text-2xl font-black" type="number"
            placeholder="00-99" min="0" max="99"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-accent)', letterSpacing: '0.2em' }}
            value={form.winning_number} onChange={(e) => setForm(f => ({ ...f, winning_number: e.target.value }))} />
          <Input data-testid="result-date-input" className="neon-input" type="date" value={form.date}
            onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} />
        </div>
        {form.winning_number && form.company_name && (
          <div className="mt-3 p-3 rounded-lg" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.1)' }}>
            <p className="text-sm" style={{ color: 'var(--neon-text-secondary)' }}>
              <strong style={{ color: 'var(--neon-accent)' }}>{form.company_name}</strong> ka result <strong style={{ color: '#FFD700', fontSize: '1.3em' }}>{form.winning_number.padStart(2, '0')}</strong> declare hoga
            </p>
            <p className="text-[10px] mt-1" style={{ color: 'var(--neon-text-muted)' }}>
              Auto match: Jodi={form.winning_number.padStart(2, '0')} | Single={form.winning_number[form.winning_number.length - 1]} | Hurf A={form.winning_number.padStart(2, '0')[0]}, B={form.winning_number[form.winning_number.length - 1]}
            </p>
          </div>
        )}
        <button data-testid="declare-result-btn" onClick={declare} className="btn-neon-primary mt-3 text-sm w-full">
          Declare Result
        </button>
      </SectionCard>

      {/* Preview Dialog */}
      {preview && (
        <SectionCard title={`Preview: Result ${preview.winning_number}`} icon={Eye}>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Bets</p>
                <p className="font-bold text-lg" style={{ color: 'var(--neon-primary)' }}>{preview.total_bets_amount}</p>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Winners</p>
                <p className="font-bold text-lg" style={{ color: '#01FFC3' }}>{preview.matches?.length || 0}</p>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Payout</p>
                <p className="font-bold text-lg" style={{ color: 'var(--neon-secondary)' }}>{preview.total_payout}</p>
              </div>
            </div>
            {preview.matches?.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {preview.matches.map((m, i) => (
                  <div key={i} className="p-2 rounded-lg flex items-center justify-between text-xs" style={{ background: 'var(--neon-surface)' }}>
                    <span style={{ color: 'var(--neon-text)' }}>{m.user_name} | {m.option}{m.hurf_side ? ` (${m.hurf_side})` : ''} #{m.number}</span>
                    <span style={{ color: '#01FFC3' }}>{m.amount} × {m.multiplier} = <strong>{m.winnings}</strong></span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3">
              <button data-testid="confirm-result-final" onClick={() => confirmResult(preview.id)}
                className="flex-1 btn-neon-primary text-sm">Confirm & Pay Winners</button>
              <button onClick={() => setPreview(null)}
                className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-secondary)' }}>Cancel</button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Results History */}
      <SectionCard title="Results History" icon={BarChart3}>
        {results.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No results</p>}
        {results.map(r => (
          <div key={r.id} data-testid={`result-${r.id}`} className="p-3 rounded-lg mb-2 flex items-center justify-between" style={{ background: 'var(--neon-surface)' }}>
            <div>
              <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{r.company_name} - {r.date}</p>
              <p className="text-sm font-black" style={{ color: 'var(--neon-accent)' }}>Result: {String(r.winning_number).padStart(2, '0')}</p>
              {r.confirmed && <p className="text-xs" style={{ color: '#01FFC3' }}>Payout: {r.total_payout} | Winners: {r.winner_count || '?'}</p>}
            </div>
            <div className="flex gap-2">
              {!r.confirmed && (
                <>
                  <button data-testid={`preview-result-${r.id}`} onClick={() => loadPreview(r.id)}
                    className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>
                    {previewLoading ? '...' : 'Preview'}
                  </button>
                  <button data-testid={`confirm-result-${r.id}`} onClick={() => setConfirmDialog({ open: true, id: r.id, action: 'confirm' })}
                    className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(1,255,195,0.15)', color: '#01FFC3' }}>Confirm</button>
                </>
              )}
              {r.confirmed && (
                <button data-testid={`revert-result-${r.id}`} onClick={() => setConfirmDialog({ open: true, id: r.id, action: 'revert' })}
                  className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>Revert</button>
              )}
            </div>
          </div>
        ))}
      </SectionCard>

      {/* Quick Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={() => setConfirmDialog({ open: false, id: null, action: '' })}>
        <DialogContent style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle style={{ color: 'var(--neon-secondary)', fontFamily: 'var(--font-heading)' }}>
              {confirmDialog.action === 'confirm' ? 'Confirm Result?' : 'Revert Result?'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm py-2" style={{ color: 'var(--neon-text-secondary)' }}>
            {confirmDialog.action === 'confirm'
              ? 'Sabhi options (Jodi, Single, Hurf, Cross, Patti) ke winners ko automatically payout hoga.'
              : 'Sabhi payouts revert ho jayenge. Winners ki winnings wapas kat jayegi.'}
          </p>
          <div className="flex gap-3">
            <button data-testid="confirm-dialog-yes" onClick={() => { confirmDialog.action === 'confirm' ? confirmResult(confirmDialog.id) : revert(confirmDialog.id); setConfirmDialog({ open: false, id: null, action: '' }); }}
              className="flex-1 btn-neon-primary text-sm">Yes, {confirmDialog.action}</button>
            <button onClick={() => setConfirmDialog({ open: false, id: null, action: '' })}
              className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-secondary)' }}>Cancel</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Analytics =====
function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [pl, setPl] = useState(null);
  const [startDate, setStartDate] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [endDate, setEndDate] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [analyticsDate, setAnalyticsDate] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const fetchAnalytics = useCallback((companyId, date) => {
    let url = `/admin/bet-analytics?date=${date || analyticsDate}`;
    if (companyId) url += `&company_id=${companyId}`;
    api.get(url).then(r => setData(r.data)).catch(() => {});
  }, [analyticsDate]);

  useEffect(() => {
    fetchAnalytics('', analyticsDate);
    api.get('/admin/companies').then(r => setCompanies(r.data.companies || [])).catch(() => {});
  }, [analyticsDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompanyFilter = (cid) => {
    setSelectedCompany(cid);
    fetchAnalytics(cid, analyticsDate);
  };

  const fetchPL = async () => {
    try { const res = await api.get(`/admin/profit-loss?start_date=${startDate}&end_date=${endDate}`); setPl(res.data); }
    catch { toast.error('Failed'); }
  };
  useEffect(() => { fetchPL(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <SectionCard title="Profit & Loss" icon={TrendingUp}>
        <div className="flex gap-3 mb-4 flex-wrap">
          <input data-testid="pl-start-date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="neon-input p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }} />
          <input data-testid="pl-end-date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="neon-input p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }} />
          <button data-testid="fetch-pl-btn" onClick={fetchPL} className="btn-neon-primary text-sm px-4">Fetch</button>
        </div>
        {pl && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Bets</p>
              <p className="text-xl font-bold" style={{ color: 'var(--neon-primary)' }}>{pl.total_bets}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Total Payouts</p>
              <p className="text-xl font-bold" style={{ color: 'var(--neon-secondary)' }}>{pl.total_payouts}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Profit</p>
              <p className="text-xl font-bold" style={{ color: pl.profit >= 0 ? '#01FFC3' : '#FF2A6D' }}>{pl.profit}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Deposits / Withdrawals</p>
              <p className="text-sm font-bold" style={{ color: 'var(--neon-text)' }}>{pl.total_deposits} / {pl.total_withdrawals}</p>
            </div>
          </div>
        )}
        {pl?.daily?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>Daily Breakdown</p>
            {pl.daily.map(d => (
              <div key={d.date} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--neon-surface)' }}>
                <span className="text-xs font-bold" style={{ color: 'var(--neon-text)' }}>{d.date}</span>
                <div className="flex gap-4 text-xs">
                  <span style={{ color: 'var(--neon-primary)' }}>Bets: {d.bets}</span>
                  <span style={{ color: 'var(--neon-secondary)' }}>Pay: {d.payouts}</span>
                  <span style={{ color: d.profit >= 0 ? '#01FFC3' : '#FF2A6D' }}>P&L: {d.profit}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {data && (
        <>
          <SectionCard title="Top Numbers (Date-wise)" icon={BarChart3}>
            <div className="flex gap-2 mb-3 flex-wrap">
              <input data-testid="analytics-date" type="date" value={analyticsDate}
                onChange={(e) => { setAnalyticsDate(e.target.value); }}
                className="flex-1 p-2 rounded-lg text-sm"
                style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }} />
              <select data-testid="company-filter-select" value={selectedCompany}
                onChange={(e) => handleCompanyFilter(e.target.value)}
                className="flex-1 p-2 rounded-lg text-sm font-bold"
                style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <option value="">All Companies</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            {data?.date && <p className="text-xs mb-2" style={{ color: 'var(--neon-accent)' }}>Showing bets for: {data.date}</p>}
            {data.top_numbers.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: 'var(--neon-text-muted)' }}>No bets found for this date</p>
            ) : (
            <div className="space-y-2">
              {data.top_numbers.map((n, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--neon-surface)' }}>
                  <span className="font-bold" style={{ color: 'var(--neon-accent)' }}>#{n.number}</span>
                  <div className="flex gap-4 text-xs">
                    <span style={{ color: 'var(--neon-text-secondary)' }}>{n.count} bets</span>
                    <span style={{ color: 'var(--neon-primary)' }}>{n.total} coins</span>
                  </div>
                </div>
              ))}
            </div>
            )}
          </SectionCard>
          <SectionCard title="Bets by Company (Today)" icon={Building2}>
            <div className="space-y-2">
              {data.by_company.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: 'var(--neon-surface)' }}>
                  <span className="font-bold" style={{ color: 'var(--neon-text)' }}>{c.company || 'Unknown'}</span>
                  <div className="flex gap-4 text-xs">
                    <span style={{ color: 'var(--neon-text-secondary)' }}>{c.count} bets</span>
                    <span style={{ color: 'var(--neon-primary)' }}>{c.total} coins</span>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

// ===== Appearance =====


function SADepositBonusSettings() {
  const [bonus, setBonus] = useState(5);
  useEffect(() => { api.get('/admin/sa-deposit-bonus').then(r => setBonus(r.data.bonus_per_100 ?? 5)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put('/admin/sa-deposit-bonus', { bonus_per_100: bonus }); toast.success('Saved!'); } catch { toast.error('Failed'); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>
        Sub-Admin deposit पर extra coins (per ₹100). Example: ₹5 set → SA deposits ₹500 → gets ₹525
      </p>
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Bonus per ₹100</Label>
          <Input data-testid="sa-deposit-bonus-input" className="neon-input" type="number" min={0}
            value={bonus} onChange={e => setBonus(parseInt(e.target.value) || 0)} />
        </div>
        <button data-testid="save-sa-deposit-bonus" onClick={save} className="btn-neon-primary text-sm px-4 py-2">Save</button>
      </div>
    </div>
  );
}


function SACompanySettings() {
  const [s, setS] = useState({ add_company_charge: 0, first_company_free: true });
  useEffect(() => { api.get('/admin/sa-company-settings').then(r => setS(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put('/admin/sa-company-settings', s); toast.success('Saved!'); } catch { toast.error('Failed'); }
  };
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Company Add Charge (coins)</Label>
        <Input data-testid="sa-company-charge" className="neon-input" type="number" value={s.add_company_charge}
          onChange={e => setS(x => ({ ...x, add_company_charge: parseInt(e.target.value) || 0 }))} />
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={s.first_company_free} onChange={e => setS(x => ({ ...x, first_company_free: e.target.checked }))} className="accent-[var(--neon-primary)]" />
        <span className="text-sm" style={{ color: 'var(--neon-text)' }}>First Company Free</span>
      </label>
      <button data-testid="save-sa-company-settings" onClick={save} className="btn-neon-primary text-sm">Save</button>
    </div>
  );
}


function AdminAppearance() {
  const [bannerText, setBannerText] = useState('');
  const [bannerActive, setBannerActive] = useState(true);
  const [themeId, setThemeId] = useState(1);
  const [fontId, setFontId] = useState(1);
  const [popup, setPopup] = useState({ title: '', message: '', active: false });
  const [upis, setUpis] = useState([]);
  const [newUpi, setNewUpi] = useState('');
  const [walletSettings, setWalletSettings] = useState({ min_deposit: 0, min_withdraw: 0 });
  // Helpline
  const [helpline, setHelpline] = useState({ mode: 'whatsapp', whatsapp_number: '', telegram_link: '', faq_items: [], ai_system_prompt: '' });
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  useEffect(() => {
    Promise.all([
      api.get('/admin/banner'), api.get('/settings'), api.get('/settings/login-popup'),
      api.get('/admin/upis'), api.get('/admin/wallet-settings'), api.get('/admin/helpline')
    ]).then(([b, s, p, u, ws, h]) => {
      setBannerText(b.data.text || ''); setBannerActive(b.data.active !== false);
      setThemeId(s.data.theme?.theme_id || 1); setFontId(s.data.font?.font_id || 1);
      setPopup(p.data);
      setUpis(u.data.upis || []);
      setWalletSettings({ min_deposit: ws.data.min_deposit || 0, min_withdraw: ws.data.min_withdraw || 0 });
      setHelpline(h.data);
    }).catch(() => {});
  }, []);

  const addUpi = async () => {
    if (!newUpi.trim()) return;
    try {
      const res = await api.post('/admin/upis', { upi_id: newUpi.trim() });
      setUpis(res.data.upis); setNewUpi(''); toast.success('UPI added!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeUpi = async (idx) => {
    try {
      const res = await api.delete(`/admin/upis/${idx}`);
      setUpis(res.data.upis); toast.success('UPI removed');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Multiple UPIs */}
      <SectionCard title="Payment UPI IDs (1-20)" icon={Wallet}>
        <p className="text-xs mb-3" style={{ color: 'var(--neon-text-muted)' }}>
          Add multiple UPI IDs for users to make deposits. Users can choose any UPI.
        </p>
        <div className="flex gap-2 mb-3">
          <Input data-testid="new-upi-input" className="neon-input flex-1" placeholder="example@upi" value={newUpi} onChange={(e) => setNewUpi(e.target.value)} />
          <button data-testid="add-upi-btn" onClick={addUpi} className="btn-neon-primary text-sm px-4" disabled={upis.length >= 20}>
            <Plus size={14} className="inline mr-1" />Add
          </button>
        </div>
        <div className="space-y-2">
          {upis.length === 0 && <p className="text-xs py-2" style={{ color: 'var(--neon-text-muted)' }}>No UPI IDs added yet</p>}
          {upis.map((upi, idx) => (
            <div key={idx} data-testid={`upi-item-${idx}`} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
              <span className="text-sm font-bold" style={{ color: 'var(--neon-accent)' }}>{upi}</span>
              <button data-testid={`remove-upi-${idx}`} onClick={() => removeUpi(idx)} className="text-xs px-2 py-1 rounded" style={{ color: 'var(--neon-secondary)' }}>Remove</button>
            </div>
          ))}
        </div>
        <p className="text-xs mt-2" style={{ color: 'var(--neon-text-muted)' }}>{upis.length}/20 UPIs added</p>
      </SectionCard>

      {/* Wallet Settings */}
      <SectionCard title="Wallet Settings" icon={Wallet}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Min Deposit</Label>
            <Input data-testid="min-deposit-input" className="neon-input" type="number" placeholder="0" value={walletSettings.min_deposit}
              onChange={(e) => setWalletSettings(s => ({ ...s, min_deposit: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Min Withdraw</Label>
            <Input data-testid="min-withdraw-input" className="neon-input" type="number" placeholder="0" value={walletSettings.min_withdraw}
              onChange={(e) => setWalletSettings(s => ({ ...s, min_withdraw: parseFloat(e.target.value) || 0 }))} />
          </div>
        </div>
        <button data-testid="save-wallet-settings-btn" onClick={async () => {
          try { await api.put('/admin/wallet-settings', walletSettings); toast.success('Wallet settings saved!'); }
          catch { toast.error('Failed'); }
        }} className="btn-neon-primary text-sm mt-3">Save Settings</button>
      </SectionCard>

      {/* SA Company Charge Settings */}
      <SectionCard title="SA Company Settings" icon={Building2}>
        <SACompanySettings />
      </SectionCard>

      {/* SA Deposit Bonus */}
      <SectionCard title="SA Deposit Bonus" icon={Coins}>
        <SADepositBonusSettings />
      </SectionCard>

      <SectionCard title="Login Popup Offer" icon={Bell}>
        <div className="space-y-3">
          <Input data-testid="popup-title-input" className="neon-input" placeholder="Popup title" value={popup.title} onChange={(e) => setPopup(p => ({ ...p, title: e.target.value }))} />
          <textarea data-testid="popup-message-input" className="neon-input w-full h-20 p-3 rounded-lg resize-none text-sm" placeholder="Popup message"
            value={popup.message} onChange={(e) => setPopup(p => ({ ...p, message: e.target.value }))}
            style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }} />
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="popup-active-toggle" type="checkbox" checked={popup.active} onChange={(e) => setPopup(p => ({ ...p, active: e.target.checked }))} className="accent-[var(--neon-primary)]" />
            <span className="text-sm" style={{ color: 'var(--neon-text)' }}>Active</span>
          </label>
          <button data-testid="save-popup-btn" onClick={async () => { try { await api.put('/admin/login-popup', popup); toast.success('Popup saved!'); } catch { toast.error('Failed'); } }}
            className="btn-neon-primary text-sm">Save Popup</button>
        </div>
      </SectionCard>

      <SectionCard title="Offer Banner" icon={Bell}>
        <Input data-testid="banner-text-input" className="neon-input mb-3" value={bannerText} onChange={(e) => setBannerText(e.target.value)} placeholder="Banner text" />
        <label className="flex items-center gap-2 cursor-pointer mb-3">
          <input data-testid="banner-active-toggle" type="checkbox" checked={bannerActive} onChange={(e) => setBannerActive(e.target.checked)} className="accent-[var(--neon-primary)]" />
          <span className="text-sm" style={{ color: 'var(--neon-text)' }}>Active</span>
        </label>
        <button data-testid="save-banner-btn" onClick={async () => { try { await api.put('/admin/banner', { text: bannerText, active: bannerActive }); toast.success('Banner saved!'); } catch { toast.error('Failed'); } }}
          className="btn-neon-primary text-sm">Save</button>
      </SectionCard>

      <SectionCard title="Theme" icon={Palette}>
        <div className="grid grid-cols-5 gap-2">
          {Object.entries(THEMES).map(([id, name]) => (
            <button key={id} data-testid={`theme-${id}`} className={`p-2 rounded-lg text-xs font-bold text-center transition-all ${parseInt(id) === themeId ? 'neon-border' : ''}`}
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)' }}
              onClick={async () => { try { await api.put('/admin/theme', { theme_id: parseInt(id) }); setThemeId(parseInt(id)); toast.success('Theme updated! Reload to see.'); } catch {} }}>
              {name}
            </button>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Font" icon={Palette}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(FONTS).map(([id, name]) => (
            <button key={id} data-testid={`font-${id}`} className={`p-2 rounded-lg text-sm font-bold transition-all ${parseInt(id) === fontId ? 'neon-border' : ''}`}
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)' }}
              onClick={async () => { try { await api.put('/admin/font', { font_id: parseInt(id) }); setFontId(parseInt(id)); toast.success('Font updated! Reload to see.'); } catch {} }}>
              {name}
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Helpline Settings */}
      <SectionCard title="Helpline / Chat Support" icon={Bell}>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Mode</Label>
            <select data-testid="helpline-mode" value={helpline.mode} onChange={(e) => setHelpline(h => ({ ...h, mode: e.target.value }))}
              className="neon-input w-full p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }}>
              <option value="whatsapp">WhatsApp / Telegram</option>
              <option value="faq">FAQ Chatbot (Free)</option>
              <option value="ai">AI Chatbot (GPT-5.2)</option>
            </select>
          </div>
          {(helpline.mode === 'whatsapp') && (
            <>
              <div>
                <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>WhatsApp Number (with country code)</Label>
                <Input data-testid="helpline-whatsapp" className="neon-input" placeholder="+919876543210" value={helpline.whatsapp_number || ''}
                  onChange={(e) => setHelpline(h => ({ ...h, whatsapp_number: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Telegram Link</Label>
                <Input data-testid="helpline-telegram" className="neon-input" placeholder="https://t.me/yourusername" value={helpline.telegram_link || ''}
                  onChange={(e) => setHelpline(h => ({ ...h, telegram_link: e.target.value }))} />
              </div>
            </>
          )}
          {helpline.mode === 'faq' && (
            <div className="space-y-2">
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Add FAQ questions & answers:</p>
              <div className="flex gap-2">
                <Input className="neon-input flex-1 text-sm" placeholder="Question" value={newFaq.question} onChange={e => setNewFaq(f => ({ ...f, question: e.target.value }))} />
                <Input className="neon-input flex-1 text-sm" placeholder="Answer" value={newFaq.answer} onChange={e => setNewFaq(f => ({ ...f, answer: e.target.value }))} />
                <button onClick={() => {
                  if (newFaq.question && newFaq.answer) {
                    setHelpline(h => ({ ...h, faq_items: [...(h.faq_items || []), newFaq] }));
                    setNewFaq({ question: '', answer: '' });
                  }
                }} className="btn-neon-primary text-xs px-3">Add</button>
              </div>
              {(helpline.faq_items || []).map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg text-xs" style={{ background: 'var(--neon-surface)' }}>
                  <div>
                    <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{f.question}</p>
                    <p style={{ color: 'var(--neon-text-muted)' }}>{f.answer}</p>
                  </div>
                  <button onClick={() => setHelpline(h => ({ ...h, faq_items: h.faq_items.filter((_, idx) => idx !== i) }))}
                    className="text-xs px-2" style={{ color: 'var(--neon-secondary)' }}>Remove</button>
                </div>
              ))}
            </div>
          )}
          {helpline.mode === 'ai' && (
            <div>
              <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>AI System Prompt (optional)</Label>
              <textarea data-testid="helpline-ai-prompt" className="neon-input w-full h-20 p-2 rounded-lg resize-none text-sm"
                style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }}
                placeholder="Custom instructions for AI bot..."
                value={helpline.ai_system_prompt || ''} onChange={(e) => setHelpline(h => ({ ...h, ai_system_prompt: e.target.value }))} />
            </div>
          )}
          <button data-testid="save-helpline-btn" onClick={async () => {
            try { await api.put('/admin/helpline', helpline); toast.success('Helpline settings saved!'); } catch { toast.error('Failed'); }
          }} className="btn-neon-primary text-sm w-full">Save Helpline</button>
        </div>
      </SectionCard>
    </div>
  );
}

// ===== Notifications =====
function AdminNotifications() {
  const [form, setForm] = useState({ title: '', message: '', type: 'notification' });
  const [loading, setLoading] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post('/admin/send-notification', { ...form, user_id: null }); toast.success('Sent!'); setForm({ title: '', message: '', type: 'notification' }); }
    catch { toast.error('Failed'); } finally { setLoading(false); }
  };
  return (
    <SectionCard title="Send to All Users" icon={Bell}>
      <form onSubmit={send} className="space-y-4">
        <div className="flex gap-3">
          {['notification', 'popup'].map(t => (
            <button key={t} type="button" data-testid={`notif-type-${t}`}
              className={`flex-1 py-2 rounded-lg text-sm font-bold ${form.type === t ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
              onClick={() => setForm(f => ({ ...f, type: t }))}>{t === 'notification' ? 'Notification' : 'Popup'}</button>
          ))}
        </div>
        <Input data-testid="notif-title-input" className="neon-input" required placeholder="Title" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
        <textarea data-testid="notif-message-input" className="neon-input w-full h-24 p-3 rounded-lg resize-none text-sm" required placeholder="Message"
          value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
          style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--neon-text)' }} />
        <button type="submit" data-testid="send-notif-btn" disabled={loading} className="btn-neon-primary disabled:opacity-50">{loading ? 'Sending...' : 'Send'}</button>
      </form>
    </SectionCard>
  );
}

// ===== Referral =====
function AdminReferral() {
  const [s, setS] = useState({ bonus_amount: 50, min_bets_to_unlock: 10, active: true, signup_bonus: 0 });
  useEffect(() => { api.get('/admin/referral-settings').then(r => setS(r.data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put('/admin/referral-settings', s); toast.success('Saved!'); } catch { toast.error('Failed'); } };
  return (
    <SectionCard title="Referral Settings" icon={Gift}>
      <div className="space-y-4">
        <div><Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Referral Bonus (locked)</Label>
          <Input data-testid="ref-bonus-input" className="neon-input" type="number" value={s.bonus_amount} onChange={(e) => setS(x => ({ ...x, bonus_amount: parseFloat(e.target.value) || 0 }))} /></div>
        <div><Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Min Bets to Unlock Referral Bonus</Label>
          <Input data-testid="ref-bets-input" className="neon-input" type="number" value={s.min_bets_to_unlock} onChange={(e) => setS(x => ({ ...x, min_bets_to_unlock: parseInt(e.target.value) || 0 }))} /></div>
        <div><Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-accent)' }}>Signup Bonus (new user को तुरंत मिलेगा)</Label>
          <Input data-testid="ref-signup-bonus-input" className="neon-input" type="number" value={s.signup_bonus || 0} onChange={(e) => setS(x => ({ ...x, signup_bonus: parseFloat(e.target.value) || 0 }))} /></div>
        <label className="flex items-center gap-2"><input data-testid="ref-active-toggle" type="checkbox" checked={s.active} onChange={(e) => setS(x => ({ ...x, active: e.target.checked }))} className="accent-[var(--neon-primary)]" />
          <span className="text-sm" style={{ color: 'var(--neon-text)' }}>Active</span></label>
        <button data-testid="save-ref-btn" onClick={save} className="btn-neon-primary text-sm">Save</button>
      </div>
    </SectionCard>
  );
}

// ===== Users =====
function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [ledgerUserId, setLedgerUserId] = useState(null);
  const fetch = useCallback(() => { api.get('/admin/users').then(r => setUsers((r.data.users || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { sensitivity: 'base' })))).catch(() => {}); }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const deleteUser = async (uid) => {
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success('User deleted');
      setDeleteConfirm(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <SectionCard title={`All Users (${users.length})`} icon={Users}>
      <WalletLedgerDialog open={!!ledgerUserId} onClose={() => setLedgerUserId(null)} userId={ledgerUserId} endpoint="/admin/user-ledger" />
      {users.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No users</p>}
      <div className="space-y-2">
        {users.map(u => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <UserAvatar name={u.name} size={36} />
                <div className="flex-1">
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{u.name}</p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{u.email} {u.mobile ? `| ${u.mobile}` : ''}</p>
                  {u.sub_admin_name && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold mt-0.5 inline-block"
                      style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>
                      SA: {u.sub_admin_name}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-accent)' }}>{u.wallet_balance || 0}</p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Bets: {u.total_bets || 0}</p>
                </div>
                <button
                  data-testid={`ledger-user-${u.id}`}
                  onClick={() => setLedgerUserId(u.id)}
                  title="Wallet History"
                  className="p-1.5 rounded-lg"
                  style={{ background: 'rgba(5,217,232,0.12)', color: 'var(--neon-primary)' }}
                >
                  <FileBarChart size={14} />
                </button>
                {deleteConfirm === u.id ? (
                  <div className="flex gap-1">
                    <button data-testid={`confirm-delete-${u.id}`} onClick={() => deleteUser(u.id)}
                      className="px-2 py-1 rounded text-xs font-bold" style={{ background: 'rgba(255,42,109,0.2)', color: '#FF2A6D' }}>
                      Confirm
                    </button>
                    <button onClick={() => setDeleteConfirm(null)}
                      className="px-2 py-1 rounded text-xs font-bold" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-muted)' }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button data-testid={`delete-user-${u.id}`} onClick={() => setDeleteConfirm(u.id)}
                    className="p-1.5 rounded-lg" style={{ color: 'var(--neon-secondary)' }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ===== Fraud Detection =====
function AdminFraudLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const fetchData = useCallback(() => {
    Promise.all([api.get('/admin/fraud-logs'), api.get('/admin/fraud-stats')]).then(([lr, sr]) => {
      setLogs(lr.data.logs);
      setStats(sr.data);
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4">
      {/* Fraud Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-xl" style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,42,109,0.2)' }}>
          <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>Total Fraud Attempts</p>
          <p data-testid="fraud-total" className="text-3xl font-black mt-1" style={{ fontFamily: 'var(--font-heading)', color: '#FF2A6D' }}>
            {stats?.total_fraud_attempts || 0}
          </p>
        </div>
        <div className="p-4 rounded-xl" style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,42,109,0.2)' }}>
          <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--neon-text-muted)' }}>Repeat Offenders</p>
          <p data-testid="fraud-repeat" className="text-3xl font-black mt-1" style={{ fontFamily: 'var(--font-heading)', color: '#FF2A6D' }}>
            {stats?.repeat_offenders?.length || 0}
          </p>
        </div>
      </div>

      {/* Repeat Offenders */}
      {stats?.repeat_offenders?.length > 0 && (
        <SectionCard title="Repeat Offenders" icon={AlertOctagon}>
          <div className="space-y-2">
            {stats.repeat_offenders.map((o, i) => (
              <div key={i} data-testid={`offender-${i}`} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,42,109,0.08)', border: '1px solid rgba(255,42,109,0.15)' }}>
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{o.user_name}</p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{o.user_email}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg" style={{ color: '#FF2A6D' }}>{o.attempts}x</p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>attempts</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Fraud Log */}
      <SectionCard title="Fraud Activity Log" icon={ShieldAlert}>
        {logs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No fraud attempts detected</p>}
        <div className="space-y-2">
          {logs.map((log, i) => (
            <div key={log.id || i} data-testid={`fraud-log-${i}`} className="p-3 rounded-lg"
              style={{ background: 'var(--neon-surface)', borderLeft: '3px solid #FF2A6D' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>
                    <ShieldAlert size={12} className="inline mr-1" style={{ color: '#FF2A6D' }} />
                    {log.user_name}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{log.user_email}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded font-bold" style={{ background: 'rgba(255,42,109,0.15)', color: '#FF2A6D' }}>
                  DUPLICATE UTR
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                <div>
                  <p style={{ color: 'var(--neon-text-muted)' }}>UTR Number</p>
                  <p className="font-bold" style={{ color: 'var(--neon-accent)' }}>{log.utr_number}</p>
                </div>
                <div>
                  <p style={{ color: 'var(--neon-text-muted)' }}>Amount</p>
                  <p className="font-bold" style={{ color: 'var(--neon-primary)' }}>{log.amount}</p>
                </div>
              </div>
              {log.original_user_name && (
                <p className="text-xs p-2 rounded" style={{ background: 'var(--neon-bg)', color: 'var(--neon-text-secondary)' }}>
                  Original UTR used by: <strong style={{ color: 'var(--neon-accent)' }}>{log.original_user_name}</strong> (Status: {log.original_tx_status})
                </p>
              )}
              <p className="text-xs mt-2" style={{ color: 'var(--neon-text-muted)' }}>{new Date(log.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ===== Sub-Admin Management =====

// ===== Commission Settings =====
function AdminCommission() {
  const [settings, setSettings] = useState({ default_percent: 20, settlement_mode: 'per_result', settlement_enabled: true, loss_mode: 'no_commission', loss_enabled: true });
  const [logs, setLogs] = useState([]);
  const [subAdmins, setSubAdmins] = useState([]);

  const fetchAll = useCallback(() => {
    api.get('/admin/commission-settings').then(r => setSettings(r.data)).catch(() => {});
    api.get('/admin/commission-history').then(r => setLogs(r.data.logs || [])).catch(() => {});
    api.get('/admin/sub-admins').then(r => setSubAdmins(r.data.sub_admins || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async () => {
    try {
      await api.put('/admin/commission-settings', settings);
      toast.success('Commission settings saved');
    } catch { toast.error('Failed'); }
  };

  const setSACommission = async (saId, percent) => {
    try {
      await api.put(`/admin/sub-admins/${saId}/commission`, { commission_percent: percent, commission_override: true });
      toast.success('Sub-Admin commission set');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      {/* Global Settings */}
      <SectionCard title="Commission Settings" icon={TrendingUp}>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Default Commission %</Label>
            <Input data-testid="commission-percent" className="neon-input w-32" type="number"
              value={settings.default_percent} onChange={(e) => setSettings(s => ({ ...s, default_percent: parseFloat(e.target.value) || 0 }))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Settlement Mode</Label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'per_result', label: 'Per Result' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
              ].map(m => (
                <button key={m.value} data-testid={`settlement-${m.value}`}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${settings.settlement_mode === m.value ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
                  onClick={() => setSettings(s => ({ ...s, settlement_mode: m.value }))}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.settlement_enabled}
                onChange={(e) => setSettings(s => ({ ...s, settlement_enabled: e.target.checked }))} />
              <span className="text-sm" style={{ color: 'var(--neon-text)' }}>Settlement Enabled</span>
            </label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest mb-2 block" style={{ color: 'var(--neon-text-muted)' }}>Loss Mode</Label>
            <div className="flex gap-2">
              {[
                { value: 'no_commission', label: 'No Commission on Loss' },
                { value: 'carry_forward', label: 'Carry Forward' },
              ].map(m => (
                <button key={m.value} data-testid={`loss-${m.value}`}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${settings.loss_mode === m.value ? 'btn-neon-primary' : 'btn-neon-secondary'}`}
                  onClick={() => setSettings(s => ({ ...s, loss_mode: m.value }))}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.loss_enabled}
                onChange={(e) => setSettings(s => ({ ...s, loss_enabled: e.target.checked }))} />
              <span className="text-sm" style={{ color: 'var(--neon-text)' }}>Loss Handling Enabled</span>
            </label>
          </div>
          <button data-testid="save-commission" onClick={saveSettings} className="btn-neon-primary text-sm">Save Settings</button>
        </div>
      </SectionCard>

      {/* Per Sub-Admin Commission */}
      <SectionCard title="Per Sub-Admin Commission" icon={Users}>
        <p className="text-xs mb-3" style={{ color: 'var(--neon-text-muted)' }}>Set custom commission % for individual Sub-Admins (overrides default)</p>
        <div className="space-y-2">
          {subAdmins.map(sa => (
            <div key={sa.id} className="p-3 rounded-lg flex items-center justify-between" style={{ background: 'var(--neon-surface)' }}>
              <div>
                <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{sa.name}</p>
                <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>{sa.referral_code} | Users: {sa.user_count}</p>
              </div>
              <div className="flex items-center gap-2">
                <Input className="neon-input w-16 text-xs text-center" type="number" placeholder={`${settings.default_percent}`}
                  defaultValue={sa.commission_percent || ''}
                  onChange={(e) => { sa._tempPct = parseFloat(e.target.value) || 0; }} />
                <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>%</span>
                <button className="text-xs px-2 py-1 rounded font-bold" style={{ background: 'rgba(5,217,232,0.1)', color: 'var(--neon-primary)' }}
                  onClick={() => setSACommission(sa.id, sa._tempPct || sa.commission_percent || settings.default_percent)}>
                  Set
                </button>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Commission History */}
      <SectionCard title="Commission History" icon={BarChart3}>
        {logs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No commission logs yet</p>}
        <div className="space-y-2">
          {logs.map(l => (
            <div key={l.id} className="p-3 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{l.sub_admin_name}</p>
                <span className="font-bold text-sm" style={{ color: l.type === 'profit' ? '#01FFC3' : 'var(--neon-secondary)' }}>
                  {l.type === 'profit' ? `-${l.commission_amount}` : l.type}
                </span>
              </div>
              <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>
                {l.company_name} | {l.date} | Bets: {l.total_bets} | Won: {l.total_winnings} | Profit: {l.profit} | {l.commission_percent}%
              </p>
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}


function AdminSubAdmins() {
  const [subAdmins, setSubAdmins] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [viewUsersFor, setViewUsersFor] = useState(null);
  const [saUsers, setSaUsers] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchSAs = useCallback(() => {
    api.get('/admin/sub-admins').then(r => setSubAdmins(r.data.sub_admins)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchSAs();
    const interval = setInterval(fetchSAs, 5000);
    return () => clearInterval(interval);
  }, [fetchSAs]);

  const createSA = async () => {
    if (!form.name || !form.phone || !form.email || !form.password) { toast.error('All fields required (Name, Mobile, Email, Password)'); return; }
    setLoading(true);
    try {
      const res = await api.post('/admin/sub-admins', form);
      toast.success(`Sub-Admin created! Referral Code: ${res.data.sub_admin.referral_code}`);
      setForm({ name: '', phone: '', email: '', password: '' });
      fetchSAs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const toggleSA = async (id) => {
    try {
      const res = await api.put(`/admin/sub-admins/${id}/toggle`);
      toast.success(res.data.message);
      fetchSAs();
    } catch { toast.error('Failed'); }
  };

  const deleteSA = async (id) => {
    try {
      await api.delete(`/admin/sub-admins/${id}`);
      toast.success('Sub-Admin deleted');
      setDeleteConfirm(null);
      fetchSAs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewUsers = async (saId) => {
    if (viewUsersFor === saId) { setViewUsersFor(null); return; }
    try {
      const res = await api.get(`/admin/sub-admins/${saId}/users`);
      setSaUsers(res.data.users);
      setViewUsersFor(saId);
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <WalletLedgerDialog open={!!ledgerUserId} onClose={() => setLedgerUserId(null)} userId={ledgerUserId} endpoint="/admin/user-ledger" />
      {/* Create Sub-Admin */}
      <SectionCard title="Create Sub-Admin" icon={UserPlus}>
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="sa-create-name" className="neon-input" placeholder="Name *" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input data-testid="sa-create-phone" className="neon-input" placeholder="Mobile Number *" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input data-testid="sa-create-email" className="neon-input" placeholder="Email *" value={form.email || ''} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input data-testid="sa-create-password" className="neon-input" type="password" placeholder="Password *" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
        </div>
        <button data-testid="sa-create-btn" onClick={createSA} disabled={loading} className="btn-neon-primary mt-3 text-sm disabled:opacity-50">
          {loading ? 'Creating...' : 'Create Sub-Admin'}
        </button>
      </SectionCard>

      {/* Sub-Admins List */}
      <SectionCard title={`Sub-Admins (${subAdmins.length})`} icon={Users}>
        {subAdmins.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No sub-admins yet</p>}
        <div className="space-y-3">
          {subAdmins.map(sa => (
            <div key={sa.id} data-testid={`sa-row-${sa.id}`} className="p-4 rounded-xl" style={{ background: 'var(--neon-surface)', border: sa.is_active ? '1px solid rgba(1,255,195,0.15)' : '1px solid rgba(255,42,109,0.15)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <UserAvatar name={sa.name} size={40} />
                  <div>
                    <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{sa.name}</p>
                    <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{sa.mobile || sa.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded" style={{
                    background: sa.is_active ? 'rgba(1,255,195,0.15)' : 'rgba(255,42,109,0.15)',
                    color: sa.is_active ? '#01FFC3' : '#FF2A6D'
                  }}>
                    {sa.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="p-2 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Code</p>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-accent)' }}>{sa.referral_code}</p>
                </div>
                <div className="p-2 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Users</p>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-primary)' }}>{sa.user_count}</p>
                </div>
                <div className="p-2 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Wallet</p>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-accent)' }}>{sa.wallet_balance}</p>
                </div>
                <div className="p-2 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>UPI</p>
                  <p className="font-bold text-xs truncate" style={{ color: sa.upi_id ? 'var(--neon-primary)' : 'var(--neon-text-muted)' }}>{sa.upi_id || 'Not set'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>User Deposits</p>
                  <p className="font-bold text-sm" style={{ color: '#01FFC3' }}>{sa.total_user_deposits}</p>
                </div>
                <div className="p-2 rounded-lg text-center" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>User Withdrawals</p>
                  <p className="font-bold text-sm" style={{ color: 'var(--neon-secondary)' }}>{sa.total_user_withdrawals}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                <button data-testid={`sa-ledger-${sa.id}`} onClick={() => setLedgerUserId(sa.id)}
                  className="py-1.5 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
                  style={{ background: 'rgba(171,71,188,0.15)', color: '#AB47BC' }}>
                  <FileBarChart size={12} /> Wallet History
                </button>
                <button data-testid={`sa-view-users-${sa.id}`} onClick={() => viewUsers(sa.id)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1"
                  style={{ background: 'rgba(5,217,232,0.1)', color: 'var(--neon-primary)' }}>
                  <Eye size={12} /> {viewUsersFor === sa.id ? 'Hide Users' : 'View Users'}
                </button>
                <button data-testid={`sa-toggle-${sa.id}`} onClick={() => toggleSA(sa.id)}
                  className="py-1.5 px-3 rounded-lg text-xs font-bold flex items-center gap-1"
                  style={{ background: sa.is_active ? 'rgba(255,42,109,0.1)' : 'rgba(1,255,195,0.1)', color: sa.is_active ? '#FF2A6D' : '#01FFC3' }}>
                  <Power size={12} /> {sa.is_active ? 'Deactivate' : 'Activate'}
                </button>
                {deleteConfirm === sa.id ? (
                  <div className="flex gap-1">
                    <button data-testid={`sa-confirm-delete-${sa.id}`} onClick={() => deleteSA(sa.id)}
                      className="px-2 py-1 rounded text-xs font-bold" style={{ background: 'rgba(255,42,109,0.2)', color: '#FF2A6D' }}>Confirm</button>
                    <button onClick={() => setDeleteConfirm(null)}
                      className="px-2 py-1 rounded text-xs font-bold" style={{ background: 'var(--neon-elevated)', color: 'var(--neon-text-muted)' }}>Cancel</button>
                  </div>
                ) : (
                  <button data-testid={`sa-delete-${sa.id}`} onClick={() => setDeleteConfirm(sa.id)}
                    className="p-1.5 rounded-lg" style={{ color: 'var(--neon-secondary)' }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>

              {/* Commission % Edit */}
              <div className="mt-3 flex items-center gap-2">
                <Label className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>Commission %:</Label>
                <Input data-testid={`sa-commission-${sa.id}`} className="neon-input w-20 text-sm text-center" type="number"
                  defaultValue={sa.commission_percent || 20}
                  onBlur={async (e) => {
                    const pct = parseInt(e.target.value) || 20;
                    try {
                      await api.put(`/admin/sub-admins/${sa.id}/commission`, { commission_percent: pct });
                      toast.success(`${sa.name} commission = ${pct}%`);
                    } catch { toast.error('Failed'); }
                  }} />
                <span className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>(Profit/Loss share)</span>
              </div>

              {/* Expanded Users */}
              {viewUsersFor === sa.id && (
                <div className="mt-3 p-3 rounded-lg space-y-2" style={{ background: 'var(--neon-bg)' }}>
                  <p className="text-xs font-bold mb-2" style={{ color: 'var(--neon-primary)' }}>Users under {sa.name}:</p>
                  {saUsers.length === 0 && <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>No users yet</p>}
                  {saUsers.map(u => (
                    <div key={u.id} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'var(--neon-surface)' }}>
                      <div className="flex items-center gap-2">
                        <UserAvatar name={u.name} size={28} />
                        <div>
                          <p className="text-xs font-bold" style={{ color: 'var(--neon-text)' }}>{u.name}</p>
                          <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-bold" style={{ color: 'var(--neon-accent)' }}>{u.wallet_balance || 0}</p>
                        <button
                          data-testid={`sa-user-ledger-${u.id}`}
                          onClick={() => setLedgerUserId(u.id)}
                          className="p-1 rounded"
                          style={{ background: 'rgba(5,217,232,0.12)', color: 'var(--neon-primary)' }}
                          title="Wallet History"
                        >
                          <FileBarChart size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ===== Sub-Admin Wallet Requests (for Main Admin) =====
function AdminSubAdminWallet() {
  const [reqs, setReqs] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });

  const fetchReqs = useCallback(() => {
    api.get('/admin/sub-admin-wallet-requests').then(r => setReqs(r.data.requests)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchReqs();
    const interval = setInterval(fetchReqs, 5000);
    return () => clearInterval(interval);
  }, [fetchReqs]);

  const handle = async (action, msg) => {
    try {
      await api.put(`/admin/sub-admin-wallet-requests/${dialog.id}`, { action, admin_message: msg });
      toast.success(`${action}d`);
      fetchReqs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const getScreenshotUrl = (path) => {
    if (!path) return null;
    const token = localStorage.getItem('neonplay_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}?auth=${token}`;
  };

  return (
    <div className="space-y-3">
      <ApproveRejectDialog open={dialog.open} onClose={() => setDialog({ open: false, id: null })} onSubmit={handle} title="Sub-Admin Wallet Action" />
      {reqs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No sub-admin wallet requests</p>}
      {reqs.map(r => (
        <SectionCard key={r.id}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="font-bold" style={{ color: 'var(--neon-text)' }}>{r.user_name}</p>
              <div className="flex gap-1 items-center mt-0.5">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: r.is_sub_admin_request ? 'rgba(5,217,232,0.15)' : 'rgba(171,71,188,0.15)', color: r.is_sub_admin_request ? 'var(--neon-primary)' : '#AB47BC' }}>
                  {r.is_sub_admin_request ? 'Sub-Admin' : 'SA User'}
                </span>
                {r.sa_name && !r.is_sub_admin_request && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--neon-text-muted)' }}>
                    SA: {r.sa_name}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="font-bold text-lg" style={{ color: r.type === 'add' ? '#01FFC3' : 'var(--neon-secondary)' }}>
                {r.type === 'add' ? '+' : '-'}{r.amount}
              </p>
              <p className="text-xs uppercase font-bold" style={{ color: 'var(--neon-text-muted)' }}>{r.type}</p>
            </div>
          </div>
          {r.utr_number && <p className="text-xs mb-1" style={{ color: 'var(--neon-text-secondary)' }}>UTR: <strong>{r.utr_number}</strong></p>}
          {r.screenshot_path && (
            <div className="mb-2">
              <img src={getScreenshotUrl(r.screenshot_path)} alt="Screenshot" className="max-h-32 rounded-lg border" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            </div>
          )}
          {r.type === 'withdraw' && (r.user_upi || r.user_account) && (
            <div className="p-3 rounded-lg mb-2 text-xs" style={{ background: 'var(--neon-bg)' }}>
              {r.user_upi && (
                <div className="flex items-start gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="mb-1" style={{ color: 'var(--neon-text-secondary)' }}>UPI ID:</p>
                    <p className="font-bold break-all mb-2" style={{ color: 'var(--neon-accent)' }}>{r.user_upi}</p>
                    <button
                      data-testid={`sawallet-copy-upi-${r.id}`}
                      onClick={() => { navigator.clipboard.writeText(r.user_upi); toast.success('UPI ID copied'); }}
                      className="text-[10px] px-2 py-1 rounded font-bold"
                      style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}
                    >
                      <Copy size={10} className="inline mr-1" />Copy UPI
                    </button>
                  </div>
                  <div className="shrink-0 p-2 rounded-lg bg-white" data-testid={`sawallet-qr-upi-${r.id}`}>
                    <QRCode
                      value={`upi://pay?pa=${encodeURIComponent(r.user_upi)}&pn=${encodeURIComponent(r.user_name || 'User')}&am=${r.amount}&cu=INR&tn=Mukamari%20Withdrawal`}
                      size={96}
                      level="M"
                    />
                    <p className="text-[9px] text-center mt-1 font-bold" style={{ color: '#000' }}>Scan & Pay ₹{r.amount}</p>
                  </div>
                </div>
              )}
              {r.user_account && <p style={{ color: 'var(--neon-text-secondary)' }}>Acc: <strong>{r.user_account}</strong> | IFSC: <strong>{r.user_ifsc}</strong></p>}
              {r.user_account_holder && <p style={{ color: 'var(--neon-text-secondary)' }}>Holder: <strong>{r.user_account_holder}</strong></p>}
            </div>
          )}
          {r.note && <p className="text-xs mb-2" style={{ color: 'var(--neon-text-muted)' }}>Note: {r.note}</p>}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              <span className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{new Date(r.created_at).toLocaleString()}</span>
            </div>
            {r.status === 'pending' && (
              <button data-testid={`sa-wallet-action-${r.id}`} onClick={() => setDialog({ open: true, id: r.id })}
                className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>
                Action
              </button>
            )}
          </div>
          {r.admin_message && <p className="mt-2 text-xs p-2 rounded" style={{ background: 'var(--neon-bg)', color: 'var(--neon-text-secondary)' }}>Admin msg: {r.admin_message}</p>}
        </SectionCard>
      ))}
    </div>
  );
}



// ===== Profile Changes =====
function AdminProfileChanges() {
  const [reqs, setReqs] = useState([]);
  const [dialog, setDialog] = useState({ open: false, id: null });
  const fetch = useCallback(() => { api.get('/admin/profile-change-requests').then(r => setReqs(r.data.requests)).catch(() => {}); }, []);
  useEffect(() => { fetch(); }, [fetch]);
  const handle = async (action, msg) => {
    try { await api.put(`/admin/profile-change-requests/${dialog.id}`, { action, admin_message: msg }); toast.success(`${action}d`); fetch(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="space-y-3">
      <ApproveRejectDialog open={dialog.open} onClose={() => setDialog({ open: false, id: null })} onSubmit={handle} title="Profile Change Action" />
      {reqs.length === 0 && <p style={{ color: 'var(--neon-text-muted)' }}>No requests</p>}
      {reqs.map(r => (
        <SectionCard key={r.id}>
          <div className="flex items-center justify-between mb-2">
            <div><p className="font-bold" style={{ color: 'var(--neon-text)' }}>{r.user_name}</p>
              <p className="text-xs" style={{ color: 'var(--neon-text-muted)' }}>{new Date(r.created_at).toLocaleString()}</p></div>
            <StatusBadge status={r.status} />
          </div>
          {r.reason && <p className="text-xs mb-2 p-2 rounded" style={{ background: 'var(--neon-bg)', color: 'var(--neon-accent)' }}>Reason: {r.reason}</p>}
          <div className="grid grid-cols-2 gap-2 text-xs mb-2">
            {r.new_upi_id && <p style={{ color: 'var(--neon-text-secondary)' }}>UPI: {r.new_upi_id}</p>}
            {r.new_account_number && <p style={{ color: 'var(--neon-text-secondary)' }}>Acc: {r.new_account_number}</p>}
            {r.new_account_ifsc && <p style={{ color: 'var(--neon-text-secondary)' }}>IFSC: {r.new_account_ifsc}</p>}
            {r.new_account_holder && <p style={{ color: 'var(--neon-text-secondary)' }}>Name: {r.new_account_holder}</p>}
          </div>
          {r.status === 'pending' && (
            <button data-testid={`action-profile-${r.id}`} onClick={() => setDialog({ open: true, id: r.id })}
              className="px-3 py-1 rounded text-xs font-bold" style={{ background: 'rgba(5,217,232,0.15)', color: 'var(--neon-primary)' }}>Action</button>
          )}
        </SectionCard>
      ))}
    </div>
  );
}


// ===== Top Earners & Gift =====
function AdminTopEarners() {
  const localDate = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
  const weekStart = () => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
  const [startDate, setStartDate] = useState(weekStart());
  const [endDate, setEndDate] = useState(localDate());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [giftTarget, setGiftTarget] = useState(null);
  const [giftAmount, setGiftAmount] = useState('');
  const [giftMsg, setGiftMsg] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/top-earners?start_date=${startDate}&end_date=${endDate}`);
      setData(res.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendGift = async () => {
    if (!giftTarget || !giftAmount || parseInt(giftAmount) <= 0) { toast.error('Amount enter करें'); return; }
    try {
      await api.post('/admin/gift-coins', { user_id: giftTarget.id, amount: parseInt(giftAmount), message: giftMsg || 'Bonus reward from Admin!' });
      toast.success(`${giftAmount} coins gifted!`);
      setGiftTarget(null); setGiftAmount(''); setGiftMsg('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const EarnerRow = ({ item, type }) => (
    <div className="p-3 rounded-xl flex items-center justify-between" style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="flex-1">
        <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{item.name}</p>
        <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>
          {type === 'user' ? `Bets: ₹${item.total_amount} | Won: ₹${item.total_winnings}` : `Users: ${item.total_users} | Bets: ₹${item.total_amount}`}
        </p>
      </div>
      <div className="text-right mr-3">
        <p className="text-sm font-black" style={{ color: '#01FFC3', fontFamily: 'var(--font-heading)' }}>+₹{item.profit}</p>
        <p className="text-[9px]" style={{ color: 'var(--neon-text-muted)' }}>profit</p>
      </div>
      <button data-testid={`gift-${type}-${item[type === 'user' ? 'user_id' : 'sa_id']}`}
        onClick={() => setGiftTarget({ id: item[type === 'user' ? 'user_id' : 'sa_id'], name: item.name })}
        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105"
        style={{ background: 'rgba(255,215,0,0.15)', color: '#FFD700', border: '1px solid rgba(255,215,0,0.3)' }}>
        Gift
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionCard title="Top Profit Givers" icon={Trophy}>
        <div className="flex gap-2 mb-3">
          <div className="flex-1">
            <Label className="text-[10px] mb-0.5 block" style={{ color: 'var(--neon-text-muted)' }}>From</Label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="w-full p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }} />
          </div>
          <div className="flex-1">
            <Label className="text-[10px] mb-0.5 block" style={{ color: 'var(--neon-text-muted)' }}>To</Label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="w-full p-2 rounded-lg text-sm" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }} />
          </div>
          <button onClick={fetchData} disabled={loading} className="btn-neon-primary px-4 text-sm self-end">
            {loading ? '...' : 'Go'}
          </button>
        </div>

        {data && (
          <>
            <p className="text-xs mb-2 font-bold" style={{ color: 'var(--neon-accent)' }}>
              Top Users ({data.start_date} to {data.end_date})
            </p>
            {data.top_users.length === 0 ? (
              <p className="text-xs py-3 text-center" style={{ color: 'var(--neon-text-muted)' }}>No profit-giving users in this period</p>
            ) : (
              <div className="space-y-2 mb-4">
                {data.top_users.map((u, i) => <EarnerRow key={u.user_id} item={u} type="user" />)}
              </div>
            )}

            <p className="text-xs mb-2 font-bold" style={{ color: '#AB47BC' }}>
              Top Sub-Admins
            </p>
            {data.top_sub_admins.length === 0 ? (
              <p className="text-xs py-3 text-center" style={{ color: 'var(--neon-text-muted)' }}>No profit-giving Sub-Admins in this period</p>
            ) : (
              <div className="space-y-2">
                {data.top_sub_admins.map((s, i) => <EarnerRow key={s.sa_id} item={s} type="sa" />)}
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* Gift Dialog */}
      {giftTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
          <div className="glass-card rounded-2xl p-6 w-full max-w-sm" style={{ background: 'var(--neon-bg)', border: '1px solid rgba(255,215,0,0.3)' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: '#FFD700', fontFamily: 'var(--font-heading)' }}>
              Gift Coins to {giftTarget.name}
            </h3>
            <div className="space-y-3">
              <div>
                <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Amount (coins)</Label>
                <Input data-testid="gift-amount-input" className="neon-input" type="number" min={1}
                  placeholder="Enter amount" value={giftAmount} onChange={e => setGiftAmount(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Message (optional)</Label>
                <Input data-testid="gift-msg-input" className="neon-input"
                  placeholder="Thank you for your loyalty!" value={giftMsg} onChange={e => setGiftMsg(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <button data-testid="send-gift-btn" onClick={sendGift}
                  className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'rgba(255,215,0,0.2)', color: '#FFD700', border: '1px solid rgba(255,215,0,0.4)' }}>
                  Send Gift
                </button>
                <button onClick={() => { setGiftTarget(null); setGiftAmount(''); setGiftMsg(''); }}
                  className="flex-1 py-2 rounded-lg text-sm font-bold" style={{ background: 'var(--neon-surface)', color: 'var(--neon-text-muted)' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== SA Daily Reports =====
function AdminSAReports() {
  const [date, setDate] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/sa-daily-report?date=${date}`);
      setReport(res.data);
    } catch { toast.error('Failed to load report'); }
    setLoading(false);
  };

  useEffect(() => { fetchReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatWhatsAppMsg = (r) => {
    const pnl = r.profit_loss >= 0 ? `+₹${r.profit_loss} (Profit)` : `-₹${Math.abs(r.profit_loss)} (Loss)`;
    return `👤 ${r.sa_name}\n├ Total Bets: ₹${r.total_bet_amount}\n├ Winnings: ₹${r.total_winnings}\n├ P&L: ${pnl}\n└ Active Users: ${r.active_users}/${r.total_users}`;
  };

  const shareOne = (r) => {
    const msg = `📊 Mukamari Daily Report\n📅 Date: ${date}\n\n${formatWhatsAppMsg(r)}`;
    const phone = r.sa_phone ? r.sa_phone.replace(/[^0-9]/g, '') : '';
    const phoneNum = phone.startsWith('91') ? phone : `91${phone}`;
    window.open(`https://wa.me/${phoneNum}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const shareAll = () => {
    if (!report) return;
    const lines = report.reports.filter(r => r.total_bets > 0 || r.total_users > 0).map(formatWhatsAppMsg);
    const grandPnl = report.grand_profit_loss >= 0 ? `+₹${report.grand_profit_loss}` : `-₹${Math.abs(report.grand_profit_loss)}`;
    const msg = `📊 Mukamari Daily Report\n📅 Date: ${date}\n\n${lines.join('\n\n')}\n\n━━━━━━━━━━━━━━\n💰 Grand Total\n├ Bets: ₹${report.grand_total_bets}\n├ Winnings: ₹${report.grand_total_winnings}\n└ P&L: ${grandPnl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="space-y-4">
      <SectionCard title="SA Daily Report" icon={BarChart3}>
        <div className="flex gap-2 items-end mb-4">
          <div className="flex-1">
            <Label className="text-xs mb-1 block" style={{ color: 'var(--neon-text-muted)' }}>Date</Label>
            <input data-testid="sa-report-date" type="date" className="w-full p-2 rounded-lg text-sm"
              style={{ background: 'var(--neon-surface)', color: 'var(--neon-text)', border: '1px solid rgba(255,255,255,0.1)' }}
              value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <button data-testid="sa-report-fetch" onClick={fetchReport} disabled={loading}
            className="btn-neon-primary px-5 py-2 text-sm font-bold disabled:opacity-50">
            {loading ? 'Loading...' : 'Generate'}
          </button>
        </div>

        {report && (
          <>
            {/* Grand Summary */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-surface)' }}>
                <p className="text-[10px] uppercase" style={{ color: 'var(--neon-text-muted)' }}>Total Bets</p>
                <p className="text-lg font-black" style={{ color: 'var(--neon-primary)', fontFamily: 'var(--font-heading)' }}>₹{report.grand_total_bets}</p>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-surface)' }}>
                <p className="text-[10px] uppercase" style={{ color: 'var(--neon-text-muted)' }}>Winnings</p>
                <p className="text-lg font-black" style={{ color: 'var(--neon-secondary)', fontFamily: 'var(--font-heading)' }}>₹{report.grand_total_winnings}</p>
              </div>
              <div className="p-3 rounded-lg text-center" style={{ background: 'var(--neon-surface)' }}>
                <p className="text-[10px] uppercase" style={{ color: 'var(--neon-text-muted)' }}>P&L</p>
                <p className="text-lg font-black" style={{ color: report.grand_profit_loss >= 0 ? '#01FFC3' : '#FF2A6D', fontFamily: 'var(--font-heading)' }}>
                  {report.grand_profit_loss >= 0 ? '+' : '-'}₹{Math.abs(report.grand_profit_loss)}
                </p>
              </div>
            </div>

            {/* Per Sub-Admin */}
            <div className="space-y-2">
              {report.reports.map(r => (
                <div key={r.sa_id} data-testid={`sa-report-${r.sa_id}`} className="p-3 rounded-xl"
                  style={{ background: 'var(--neon-surface)', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-bold text-sm" style={{ color: 'var(--neon-text)' }}>{r.sa_name}</p>
                      <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Users: {r.active_users}/{r.total_users} active</p>
                    </div>
                    <button data-testid={`sa-report-wa-${r.sa_id}`} onClick={() => shareOne(r)}
                      className="p-2 rounded-lg transition-all hover:scale-110" style={{ background: 'rgba(37,211,102,0.15)', color: '#25D366' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Bets ({r.total_bets})</p>
                      <p className="text-sm font-bold" style={{ color: 'var(--neon-primary)' }}>₹{r.total_bet_amount}</p>
                    </div>
                    <div>
                      <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>Winnings</p>
                      <p className="text-sm font-bold" style={{ color: 'var(--neon-secondary)' }}>₹{r.total_winnings}</p>
                    </div>
                    <div>
                      <p className="text-[10px]" style={{ color: 'var(--neon-text-muted)' }}>P&L</p>
                      <p className="text-sm font-bold" style={{ color: r.profit_loss >= 0 ? '#01FFC3' : '#FF2A6D' }}>
                        {r.profit_loss >= 0 ? '+' : '-'}₹{Math.abs(r.profit_loss)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Share All Button */}
            {report.reports.length > 0 && (
              <button data-testid="sa-report-share-all" onClick={shareAll}
                className="w-full mt-3 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                style={{ background: 'rgba(37,211,102,0.15)', border: '1px solid rgba(37,211,102,0.3)', color: '#25D366' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send All Reports via WhatsApp
              </button>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}


// ===== MAIN ADMIN PAGE =====
export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div className="min-h-screen pb-28 p-4" style={{ background: 'var(--neon-bg)' }}>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-black mb-6" style={{ fontFamily: 'var(--font-heading)', color: 'var(--neon-primary)' }}>
          Admin Control Room
        </h1>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap gap-1 mb-6 p-1 rounded-xl" style={{ background: 'var(--neon-elevated)' }}>
            {[
              { v: 'dashboard', icon: LayoutDashboard, l: 'Dashboard' },
              { v: 'sub-admins', icon: UserPlus, l: 'Sub-Admins' },
              { v: 'sa-wallet', icon: Wallet, l: 'SA Wallet' },
              { v: 'companies', icon: Building2, l: 'Companies' },
              { v: 'wallet', icon: Wallet, l: 'Wallet' },
              { v: 'fraud', icon: ShieldAlert, l: 'Fraud' },
              { v: 'results', icon: Trophy, l: 'Results' },
              { v: 'analytics', icon: BarChart3, l: 'Analytics' },
              { v: 'game', icon: Gamepad2, l: 'Game' },
              { v: 'commission', icon: TrendingUp, l: 'Commission' },
              { v: 'sa-reports', icon: FileBarChart, l: 'SA Reports' },
              { v: 'top-earners', icon: Trophy, l: 'Top Earners' },
              { v: 'users', icon: Users, l: 'Users' },
              { v: 'reset', icon: KeyRound, l: 'Password' },
              { v: 'notifications', icon: Bell, l: 'Notify' },
              { v: 'appearance', icon: Palette, l: 'Settings' },
              { v: 'referral', icon: Gift, l: 'Referral' },
              { v: 'profile', icon: UserCog, l: 'Profile' },
            ].map(t => (
              <TabsTrigger key={t.v} data-testid={`admin-tab-${t.v}`} value={t.v}
                className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg data-[state=active]:text-black"
                style={{ fontFamily: 'var(--font-heading)' }}>
                <t.icon size={11} /> {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="dashboard"><AdminDashboard onNavigate={setActiveTab} /></TabsContent>
          <TabsContent value="sub-admins"><AdminSubAdmins /></TabsContent>
          <TabsContent value="sa-wallet"><AdminSubAdminWallet /></TabsContent>
          <TabsContent value="companies"><AdminCompanies /></TabsContent>
          <TabsContent value="wallet"><AdminWallet /></TabsContent>
          <TabsContent value="fraud"><AdminFraudLogs /></TabsContent>
          <TabsContent value="results"><AdminResults /></TabsContent>
          <TabsContent value="analytics"><AdminAnalytics /></TabsContent>
          <TabsContent value="game"><AdminGameSettings /></TabsContent>
          <TabsContent value="commission"><AdminCommission /></TabsContent>
          <TabsContent value="sa-reports"><AdminSAReports /></TabsContent>
          <TabsContent value="top-earners"><AdminTopEarners /></TabsContent>
          <TabsContent value="users"><AdminUsers /></TabsContent>
          <TabsContent value="reset"><AdminPasswordReset /></TabsContent>
          <TabsContent value="notifications"><AdminNotifications /></TabsContent>
          <TabsContent value="appearance"><AdminAppearance /></TabsContent>
          <TabsContent value="referral"><AdminReferral /></TabsContent>
          <TabsContent value="profile"><AdminProfileChanges /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
